import type { ReactNode } from 'react'
import { ErrorMessage, Mtds } from 'prove-common'

type AppLayoutProps = {
  topBar?: ReactNode
  sidebar?: ReactNode
  children?: ReactNode
  isLoading?: boolean
  isError?: boolean
  isFullWidth?: boolean
  error?: unknown
  errorActionButton?: ReactNode
  loadingMessage?: string
  loadingProgress?: {
    value: number
    max: number
  }
  maxWidth?: number
  useMobileBar?: boolean
  contentLayout?: 'none' | 'grid'
}

const renderContent = ({
  children,
  contentLayout = 'grid'
}: Pick<AppLayoutProps, 'children' | 'contentLayout'>) => {
  if (contentLayout === 'none') return children

  return (
    <Mtds.Grid data-gap={8} className='min-w-0 content-start'>
      {children}
    </Mtds.Grid>
  )
}

export const AppLayout = ({
  topBar,
  sidebar,
  children,
  isLoading = false,
  isError = false,
  error,
  errorActionButton,
  loadingMessage,
  loadingProgress,
  isFullWidth = false,
  maxWidth = 800,
  useMobileBar = true,
  contentLayout
}: AppLayoutProps) => {
  return (
    <Mtds.App id={'app'} data-variant={useMobileBar ? 'mobilebar' : undefined}>
      <Mtds.App.Header className={'min-h-16'}>{topBar}</Mtds.App.Header>
      {sidebar}
      <Mtds.Card
        as={Mtds.App.Main}
        className='max-md:-mx-(--mtds-4) max-md:overflow-x-auto'
      >
        <div
          style={{
            maxWidth: isFullWidth ? 'none' : maxWidth
          }}
          className={'h-full m-auto'}
        >
          {(isLoading || isError || !!error) && (
            <div className='h-full flex justify-center items-center'>
              {isLoading && (
                <Mtds.Grid>
                  <Mtds.Spinner>
                    {loadingMessage}
                    {loadingProgress &&
                      ` (${Math.round((loadingProgress.value / loadingProgress.max) * 100)}%)`}
                  </Mtds.Spinner>
                  {loadingProgress && (
                    <Mtds.Progress
                      value={loadingProgress.value}
                      max={loadingProgress.max}
                    />
                  )}
                </Mtds.Grid>
              )}
              {(isError || !!error) && (
                <ErrorMessage error={error} actionButton={errorActionButton} />
              )}
            </div>
          )}
          {!isLoading &&
            !isError &&
            !error &&
            renderContent({
              children,
              contentLayout
            })}
        </div>
      </Mtds.Card>
    </Mtds.App>
  )
}
